import React from "react"
import dayjs from "dayjs"
import PropTypes from "prop-types"

// components
import { DatePicker as AntDatePicker } from "antd"
import ReadOnlyField from "components-design-system/form-fields/ReadOnlyField/ReadOnlyField"

// styles
import "./DatePicker.scss"

// apis
import { useUserSettings } from "api/hooks"

const DatePicker = ({
  className,
  defaultValue,
  format,
  onChange, // parent function to call
  placeholder,
  readOnly,
  value,
  ...moreProps // size, onChange
}) => {
  const { data: userSettings } = useUserSettings()
  const classNames = ["prime-date-picker"]
  if (className) classNames.push(className)

  const getFormat = () => {
    if (userSettings?.date_format) return userSettings?.date_format
    if (format) return format
    return "YYYY-MM-DD"
  }

  const handleChange = (dayJsObj) => {
    if (dayJsObj) {
      onChange(dayJsObj.format("YYYY-MM-DD"))
    } else {
      onChange(null)
    }
  }

  if (readOnly) {
    return (
      <ReadOnlyField
        placeholder={placeholder}
        className={classNames.join(" ")}
      >
        {dayjs(value).isValid() ? dayjs(value).format(getFormat()) || dayjs(defaultValue).format(getFormat()) : ""}
      </ReadOnlyField>
    )
  }

  return (
    <AntDatePicker
      defaultValue={defaultValue && dayjs(defaultValue).isValid() ? dayjs(defaultValue) : undefined}
      placeholder={placeholder}
      onChange={handleChange}
      format={getFormat()}
      className={classNames.join(" ")}
      value={dayjs(value).isValid() && value ? dayjs(value) : undefined}
      {...moreProps}
    />
  )
}

DatePicker.propTypes = {
  /** Specifies the size of the DatePicker component. */
  size: PropTypes.oneOf(["small", "middle", "large"]),

  /** If true, the DatePicker input will be disabled. */
  disabled: PropTypes.bool,

  /** If true, the DatePicker input will be read-only. */
  readOnly: PropTypes.bool,

  /** Determines the validation status of the DatePicker component. */
  status: PropTypes.oneOf(["none", "error", "warning"]),

  /** Configures the date format. If this is not set, it will be configured with the user date format setting. If the there is no user setting, the date format will default to "YYYY-MM-DD". */
  format: PropTypes.string,

  /** If true, shows the clear button on the input. */
  allowClear: PropTypes.bool,

  /** Placeholder text displayed when no date is selected. */
  placeholder: PropTypes.string,

  /** To control the value of the component. **onChange** needs to be handled to set the value after the user interacts with the component. */
  value: PropTypes.string,

  /** Default value of the DatePicker. */
  defaultValue: PropTypes.string,

  /** Custom class name for the DatePicker component. */
  className: PropTypes.string,

  /** Callback function triggered when the date changes. */
  onChange: PropTypes.func,
}

export default DatePicker
